import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttributeCategoryToken } from '@sleed/bizcommerce-api';
import { attributeCategoriesApi } from '~/lib/api/client';
import { formToToken, type AttributeCategoryFormValues } from '~/lib/schemas/attribute-category';

export const attributeCategoryKeys = {
	all: ['attribute-categories'] as const,
	list: () => [...attributeCategoryKeys.all, 'list'] as const
};

/**
 * Attribute-categories list. Mirrors the legacy admin grid's `/attributecategories/getall`
 * (loadMode 'raw'): the endpoint returns the full set in an `AttributeCategoryTokenList.items`
 * envelope — no paging.
 */
export function useAttributeCategoriesList() {
	return useQuery<AttributeCategoryToken[]>({
		queryKey: attributeCategoryKeys.list(),
		queryFn: async () => (await attributeCategoriesApi.getAll()).data.items ?? []
	});
}

/** Create / update / delete an attribute category (parity with the legacy CustomStore insert/update/remove). */
export function useAttributeCategoryMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: attributeCategoryKeys.all });

	const create = useMutation({
		mutationFn: async (form: AttributeCategoryFormValues) => (await attributeCategoriesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: AttributeCategoryFormValues) => (await attributeCategoriesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await attributeCategoriesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
